import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Pagination,
  Avatar,
  Breadcrumbs,
  Link as MUILink,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Switch, FormControlLabel } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  setSearchValue,
  fetchBuyerConnections,
  updateConnectionStatus,
  clearError,
  assignBuyerCategory,
} from "../redux/slices/buyerNetworkSlice";
import { getAllBuyerCategories } from "../redux/slices/buyerCategoriesSlice";
import FindNewBuyerModal from "./FindNewBuyerModal";
import { useNavigate } from "react-router-dom";
import BuyerChatModal from "../Components/BuyerChatModal";
import Badge from "@mui/material/Badge";
import { socket } from "../socket";
import { Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";

export default function BuyerNetwork() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [openFindBuyer, setOpenFindBuyer] = useState(false);
  const { buyers, tabValue, searchValue, loading, error, totalPages } =
    useSelector((state) => state.buyerNetwork);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [pendingCategory, setPendingCategory] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [chatOpen, setChatOpen] = useState(false);
  const [activeBuyer, setActiveBuyer] = useState(null);
  const { user } = useSelector((state) => state.user);

  const [onlineUsers, setOnlineUsers] = useState([]);
  // const currentMode = user?.mode || "seller";

  const [currentMode, setCurrentMode] = useState("seller");

  const [showKioskOnly, setShowKioskOnly] = useState(false);
  const [buyerFilter, setBuyerFilter] = useState("all");
  // default = all

  useEffect(() => {
    socket.on("onlineUsers", (users) => {
      setOnlineUsers(users);
    });

    return () => socket.off("onlineUsers");
  }, []);

  useEffect(() => {
    dispatch(fetchBuyerConnections({ page, limit: itemsPerPage }));
  }, [dispatch, page]);

  const { categories: buyerCategories, loading: categoriesLoading } =
    useSelector((state) => state.buyerCategories);

  useEffect(() => {
    dispatch(getAllBuyerCategories());
  }, [dispatch]);

  // Show toast for errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Search logic (Filter only on current page data)
  const filteredBuyers = buyers?.filter(
    (b) =>
      b.name?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.city?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.company?.toLowerCase().includes(searchValue?.toLowerCase()),
  );

  // const filteredBuyers = buyers?.filter((b) => {
  //   const matchesSearch =
  //     b.name?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.city?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.email?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.company?.toLowerCase().includes(searchValue?.toLowerCase());

  //   const isKioskBuyer = b?.buyerCategory?.toLowerCase() === "kiosk";

  //   // 🔥 agar checkbox ON hai → sirf kiosk
  //   if (showKioskOnly) {
  //     return matchesSearch && isKioskBuyer;
  //   }

  //   // default → sab
  //   return matchesSearch;
  // });
  // const filteredBuyers = buyers?.filter((b) => {
  //   const matchesSearch =
  //     b.name?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.city?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.email?.toLowerCase().includes(searchValue?.toLowerCase()) ||
  //     b.company?.toLowerCase().includes(searchValue?.toLowerCase());

  //   const isKioskBuyer = b?.buyerCategory?.toLowerCase() === "kiosk";

  //   if (buyerFilter === "kiosk") {
  //     // Sirf kiosk show
  //     return matchesSearch && isKioskBuyer;
  //   }

  //   // Default = All → kiosk hide
  //   return matchesSearch && !isKioskBuyer;
  // });

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Accepted":
        return { bg: "#dcfce7", color: "#166534", label: "Connected" };
      case "Pending":
        return { bg: "#fef3c7", color: "#92400e", label: "Pending" };
      case "Rejected":
        return { bg: "#fee2e2", color: "#991b1b", label: "Rejected" };
      default:
        return { bg: "#e5e7eb", color: "#374151", label: status };
    }
  };

  return (
    <>
      <PageTitle
        title="Buyer Network - BizBridge"
        description="Manage and grow your buyer network. Connect with buyers, assign categories, track pending invoices and communicate in real-time on BizBridge."
        keywords="buyer network, seller dashboard, B2B buyers, connect buyers, buyer categories, buyer communication, pending invoices, BizBridge seller"
        url="/seller-dashboard/buyer-network"
      />

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 1, sm: 2 },
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            width: "fit-content",
            backgroundColor: "#fff",
            ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/seller-dashboard/catalog"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Catalog
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            Buyer Network
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Typography fontWeight={700} fontSize={22}>
              Buyer Network
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Search */}
              <input
                type="text"
                placeholder="Search buyers..."
                value={searchValue}
                onChange={(e) => dispatch(setSearchValue(e.target.value))}
                style={{
                  fontSize: 15,
                  borderRadius: 6,
                  border: "1px solid #e0e0e0",
                  padding: "8px 12px",
                  outline: "none",
                  minWidth: 220,
                }}
              />
            </Box>
          </Box>

          {/* Modal */}
          <FindNewBuyerModal
            open={openFindBuyer}
            onClose={() => setOpenFindBuyer(false)}
          />

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <Grid
              container
              spacing={isMobile ? 3 : 4}
              justifyContent="flex-start"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <Box
                  onClick={() => setOpenFindBuyer(true)}
                  sx={{
                    height: "100%",
                    minHeight: 300,
                    borderRadius: 3,
                    border: "1.5px solid #e5e7eb",
                    background: "#f9fafb",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      border: "2px dashed #0b4064",
                      background: "#f0f6ff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: "#0b4064",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      mb: 1,
                    }}
                  >
                    +
                  </Box>
                  <Typography
                    fontWeight={600}
                    sx={{ color: "#0b4064", fontSize: 16 }}
                  >
                    Connect New Buyer
                  </Typography>
                </Box>
              </Grid>

              {filteredBuyers.length === 0 ? (
                <Box sx={{ width: "100%", textAlign: "center", py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {tabValue === 0
                      ? "No connected buyers found."
                      : tabValue === 1
                        ? "No pending requests."
                        : "No rejected requests."}
                  </Typography>
                </Box>
              ) : (
                filteredBuyers?.map((buyer) => (
                  <Grid key={buyer._id} item xs={12} sm={12} md={6} lg={4}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0px 6px 18px rgba(0,0,0,0.06)",
                      }}
                    >
                      <CardContent sx={{ p: 3, position: "relative" }}>
                        {/* STATUS BADGE */}
                        {(() => {
                          const statusStyle = getStatusStyle(buyer.status);
                          const isKioskBuyer =
                            buyer?.buyerCategory?.toLowerCase() === "kiosk";

                          return (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                              }}
                            >
                              {/* Kiosk Consumer Chip */}
                              {isKioskBuyer && (
                                <Chip
                                  label="Consumer"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                              {/* Status Badge */}
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  backgroundColor: statusStyle.bg,
                                  color: statusStyle.color,
                                }}
                              >
                                {statusStyle.label}
                              </Box>
                            </Box>
                          );
                        })()}

                        {/* Avatar with Online Status */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            variant="dot"
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: onlineUsers.includes(
                                  buyer.buyerId,
                                )
                                  ? "#22c55e" // green = online
                                  : "#9ca3af", // gray = offline
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                border: "2px solid white",
                              },
                            }}
                          >
                            <Avatar
                              alt={buyer.name}
                              src={
                                buyer.profileImage ||
                                "https://via.placeholder.com/60"
                              }
                              sx={{
                                width: 60,
                                height: 60,
                              }}
                            />
                          </Badge>
                        </Box>

                        {/* Name */}
                        {/* <Typography
                          fontWeight={700}
                          fontSize={16}
                          textAlign="center"
                          sx={{
                            color: buyer?.name
                              ?.toLowerCase()
                              .includes("unregister")
                              ? "red"
                              : "inherit",
                          }}
                        >
                          {buyer?.phoneVerified || buyer.phone || "Unnamed Buyer"}
                        </Typography> */}

                        <Typography
                          fontWeight={700}
                          fontSize={16}
                          textAlign="center"
                          sx={{
                            color:
                              buyer?.phoneVerified === false
                                ? "red"
                                : "inherit",
                          }}
                        >
                          {buyer?.phoneVerified === false
                            ? "Unregistered User"
                            : buyer?.phone || buyer?.name || "Unnamed Buyer"}

                          {/* { buyer.phoneVerified} */}
                        </Typography>

                        <Typography
                          fontWeight={700}
                          fontSize={16}
                          textAlign="center"
                        >
                          {buyer?.phoneVerified === false ? buyer?.phone : ""}
                        </Typography>

                        {/* Company */}
                        <Typography
                          variant="body2"
                          textAlign="center"
                          sx={{ color: "#6b7280", mb: 2 }}
                        >
                          {buyer.company || buyer.city || ""}
                        </Typography>

                        {/* Category */}
                        <Typography
                          variant="caption"
                          sx={{ color: "#9ca3af", fontWeight: 600 }}
                        >
                          ASSIGNED CATEGORY
                        </Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={
                            selectedCategories[buyer._id] ??
                            buyer.buyerCategory ??
                            ""
                          }
                          sx={{
                            mt: 0.5,
                            mb: 2,
                            borderRadius: 2,
                            fontSize: 14,
                            backgroundColor: "#fff",
                          }}
                          onChange={(e) => {
                            const newCategory = e.target.value;
                            setSelectedBuyer(buyer);
                            setPendingCategory(newCategory);
                            setConfirmOpen(true);
                          }}
                        >
                          {categoriesLoading ? (
                            <MenuItem disabled>Loading...</MenuItem>
                          ) : buyerCategories && buyerCategories.length > 0 ? (
                            buyerCategories
                              .filter((cat) => {
                                const name = cat?.name?.toLowerCase();
                                const buyerIsKiosk =
                                  buyer?.buyerCategory?.toLowerCase() ===
                                  "kiosk";

                                if (buyerIsKiosk) {
                                  // kiosk buyer → backend match "kiosk"
                                  return name === "kiosk";
                                }

                                // normal buyer → kiosk hide
                                return name !== "kiosk";
                              })
                              .map((cat) => (
                                <MenuItem key={cat._id} value={cat.name}>
                                  {/* Display label override */}
                                  {cat.name.toLowerCase() === "kiosk"
                                    ? "Kiosk (Consumer)"
                                    : cat.name}
                                </MenuItem>
                              ))
                          ) : (
                            <MenuItem disabled>No categories found</MenuItem>
                          )}
                        </Select>

                        {/* Actions */}
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            sx={{
                              bgcolor: "#eef2ff",
                              color: "#0b4064",
                              fontWeight: 600,
                              boxShadow: "none",
                              "&:hover": { bgcolor: "#e0e7ff" },
                            }}
                            onClick={() =>
                              navigate(
                                "/seller-dashboard/buyer-pending-invoices",
                                {
                                  state: { buyerId: buyer.buyerId },
                                },
                              )
                            }
                          >
                            Pending Invoices
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                            onClick={() => {
                              setActiveBuyer(buyer);
                              setChatOpen(true);
                            }}
                          >
                            Message
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )}
          {/* Pagination */}
          {!loading && buyers.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 6,
                pb: 4,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogContent>
            <Typography>
              Are you sure you want to assign category "{pendingCategory}" to "
              {selectedBuyer?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              // onClick={async () => {
              //   if (!selectedBuyer) return;
              //   const selectedCategoryObj = buyerCategories.find(
              //     (c) => c.name === pendingCategory,
              //   );
              //   const payload = {
              //     buyerEmail: selectedBuyer.email || null,
              //     buyerPhone: selectedBuyer.email ? null : selectedBuyer.phone,
              //     buyerCategory: selectedCategoryObj?._id,
              //   };
              //   try {
              //     const resultAction = await dispatch(
              //       assignBuyerCategory(payload),
              //     );
              //     if (assignBuyerCategory.fulfilled.match(resultAction)) {
              //       dispatch(fetchBuyerConnections({ page: 1, limit: 10 }));
              //       toast.success(
              //         `${selectedCategoryObj?.name} Buyer category updated`,
              //       );
              //     } else {
              //       toast.error(resultAction.payload || "Update failed");
              //     }
              //   } catch (err) {
              //     console.error(err);
              //   } finally {
              //     setConfirmOpen(false);
              //     setSelectedBuyer(null);
              //     setPendingCategory("");
              //   }
              // }}

              onClick={async () => {
                if (!selectedBuyer) return;
                const selectedCategoryObj = buyerCategories.find(
                  (c) => c.name === pendingCategory,
                );

                const payload = {
                  buyerEmail: selectedBuyer.email || null,
                  buyerPhone: selectedBuyer.phone || null, 
                  buyerCategory: selectedCategoryObj?._id,
                };

                if (!payload.buyerEmail && !payload.buyerPhone) {
                  toast.error("Buyer has no email or phone to identify them");
                  setConfirmOpen(false);
                  return;
                }

                try {
                  const resultAction = await dispatch(
                    assignBuyerCategory(payload),
                  );
                  if (assignBuyerCategory.fulfilled.match(resultAction)) {
                    dispatch(fetchBuyerConnections({ page: 1, limit: 10 }));
                    toast.success(
                      `${selectedCategoryObj?.name} Buyer category updated`,
                    );
                  } else {
                    toast.error(resultAction.payload || "Update failed");
                  }
                } catch (err) {
                  console.error(err);
                } finally {
                  setConfirmOpen(false);
                  setSelectedBuyer(null);
                  setPendingCategory("");
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <BuyerChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          buyer={activeBuyer}
          currentUser={user?._id}
          currentMode={currentMode}
          onlineUsers={onlineUsers}
        />
      </Box>
    </>
  );
}
